import type { ReactNode } from 'react';

/**
 * Esqueleto padrão das telas (molde reutilizável) — LEIA ANTES DE CRIAR TELA NOVA.
 *
 * ## Por que existe
 * O conteúdo estava mais escuro que a casca: sidebar e topbar sentam em
 * `bg-superficie`, mas indicadores e tabela sentavam direto no `bg-fundo` da
 * página, com só a caixa de filtros num card. Resultado: a tela parecia outro
 * produto. A partir de agora TUDO que vem depois do cabeçalho — indicadores,
 * filtros, tabela — mora dentro de UM painel de `bg-superficie`, com borda e
 * raio, do mesmo jeito que a casca. Nada de card solto boiando no fundo cru.
 *
 * ## Onde cada coisa mora
 *  - **Fora do painel** (sobre o `fundo` direto): só o `<header>` — título,
 *    subtítulo, e o slot `acoes` com a ação primária da tela.
 *  - **Dentro do painel**: `filtros` (a barra de ferramentas), `indicadores`
 *    (cards de KPI) e `children` (abas, tabela, o resto). Cada um já traz seu
 *    próprio card com borda quando faz sentido (`CartaoKpi`, `Tabela`) — o
 *    painel é a MOLDURA, não substitui o card de dentro.
 *
 * ## Barra vs. gaveta de filtros
 * Regra de decisão, não gosto pessoal: **na barra fica o que se mexe em toda
 * consulta (busca, período, ações de visão); na gaveta, o que se mexe em uma
 * consulta a cada dez** (CNPJ, NIRE, faixa de capital, sócio nomeado — tudo
 * que é específico e usado às vezes). Cadastro básico não pede mais que isso
 * na barra — sete filtros lado a lado é poluição, não produtividade.
 *
 * ## A barra é UMA linha, com alturas iguais
 * Todo controle da barra usa `h-11`. NENHUM controle da barra tem rótulo
 * visível empilhado acima — rótulo empilhado quebra a linha de base e é o
 * "desalinhado" que se via antes. Quem identifica o controle é o
 * `placeholder`, o valor já escolhido, ou o `aria-label` (esse continua
 * obrigatório, é o que atende leitor de tela). Se um controle SÓ faz sentido
 * com rótulo visível ao lado (`Campo`), ele não pertence à barra — desce para
 * a gaveta, que tem espaço de sobra para isso.
 *
 * ## Ação primária
 * Vai no slot `acoes`, no canto superior direito, como botão em destaque —
 * "+ Novo X". Sem tela de cadastro ainda? O botão fica desabilitado com
 * `title` explicando quando chega — nunca aponta para uma rota que não
 * existe.
 *
 * ## Proporção: o que aparece é proporcional ao que existe
 * Regra que vale para toda tela nova, não só para quem tem 130 registros e
 * seis dígitos de faturamento — um cadastro básico com um punhado de linhas
 * não pede o mesmo aparato, e montá-lo do mesmo jeito faz a tela parecer
 * inacabada, não sofisticada. Quatro consequências diretas:
 *  - **KPI só com dado.** Lista vazia não ganha cartão nenhum — quatro
 *    cartões dizendo "0" ocupam altura sem informar nada.
 *  - **Aba só com conteúdo real.** Um recorte que fica vazio junto com a
 *    lista (um "Resumo" sem nada a resumir) não é aba, é clique perdido —
 *    tela sem dado mostra a aba principal direto, sem barra de abas.
 *  - **Chrome colapsado.** Controle que muda a APARÊNCIA (colunas
 *    visíveis, lista/cards, esconder indicador, atualizar) mora atrás de UM
 *    menu (`Dropdown`); só o que muda o RESULTADO fica solto na barra.
 *  - **Na barra, só o que muda o resultado.** Busca, período, e um filtro
 *    de uso diário (quando existir) ficam soltos; o resto — inclusive o
 *    gatilho da gaveta — é o suficiente. Ver a consulta de sociedades
 *    (`paginas/sociedades.tsx`) como referência das quatro juntas.
 *
 * Puramente estrutural: nenhuma regra de domínio, nenhuma cor literal — só
 * token, para o tema claro/escuro funcionar sem retoque.
 *
 * Padrão herdado do projeto Isotec/Adonai, que já amadureceu em produção. Não
 * reimplementar cabeçalho por tela.
 */
export interface PaginaShellProps {
  titulo: ReactNode;
  subtitulo?: ReactNode;
  /** Ações da direita: a ação primária da tela ("+ Novo X"), exportar, atualizar. */
  acoes?: ReactNode;
  /** Cards de resumo. Só com número real do servidor — nada de enfeite. */
  indicadores?: ReactNode;
  /** A barra de ferramentas: uma linha, `h-11`, sem rótulo empilhado. */
  filtros?: ReactNode;
  children: ReactNode;
}

export function PaginaShell({
  titulo,
  subtitulo,
  acoes,
  indicadores,
  filtros,
  children,
}: PaginaShellProps) {
  return (
    <div className="flex flex-col gap-4">
      {/*
        O painel: TUDO senta aqui, cabeçalho incluído, em `bg-superficie` com
        borda e raio — a mesma linguagem da casca (sidebar e topbar).

        O título e a ação primária ficavam FORA, flutuando sobre o fundo. A
        tela virava duas peças soltas em vez de uma, e a ação primária, sem
        painel atrás, parecia não pertencer a lugar nenhum. É assim que o
        Adonai e o Isotec fazem, e é o que dá a leitura de bloco único.
      */}
      <div className="border-tinta-100 bg-superficie sombra-cartao flex flex-col gap-4 rounded-xl border p-4 md:p-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-tinta-900 text-xl font-bold md:text-2xl">{titulo}</h1>
            {subtitulo && <p className="text-tinta-500 text-sm">{subtitulo}</p>}
          </div>
          {acoes && <div className="flex items-center gap-2">{acoes}</div>}
        </header>

        {/* A barra de ferramentas é uma LAJE, não controles soltos: fundo,
            borda, raio e padding próprios, como em toolbar.tsx/page-shell.tsx
            do isotec. Sem isso os controles boiam direto sobre o painel e a
            tela perde o degrau de camada. O embrulho mora AQUI (no molde), não
            na tela — é o que faz a próxima tela nascer com a laje. */}
        {filtros && (
          <div className="border-tinta-100 bg-superficie-2 flex flex-col gap-3 rounded-xl border p-3">
            {filtros}
          </div>
        )}

        {/* Quatro colunas só a partir de `xl`: em telas médias o card fica
            estreito demais e valor em moeda estoura a caixa. */}
        {indicadores && (
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">{indicadores}</div>
        )}

        {children}
      </div>
    </div>
  );
}
